import { inject, Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, filter, Observable, of, throwError } from 'rxjs';
import { NotificationService } from '../services/notifications.service';
import { SessionStorageService } from '../services/session-storage.service';
import { LoginService } from '../pages/login/services/login.service';
import { environment } from '@env/environment.local';
import { AuthService } from './auth.service';
import { AuthRefreshGate } from './auth-refresh-gate.service';

/**
 * Toggle de logging diagnóstico para el bug de expulsión de sesión.
 * Dejar en `true` mientras cazamos el race entre el preload-queue y el
 * refresh de token. Apagar antes de mergear a producción.
 */
const HTTP_AUTH_DEBUG = false;

/**
 * Nombre del header de correlación (definido en el módulo onboarding).
 * Lo replicamos como literal acá para NO acoplar la capa común global
 * a un feature module. Mismo valor que `CORRELATION_HEADER`.
 */
const CORRELATION_HEADER = 'X-Correlation-Id';

/**
 * Log estructurado y greppeable. Prefijo fijo `[HTTP-AUTH]` para filtrar
 * en la consola. Siempre incluí `cid` (correlationId) + `url` para cruzar
 * estas trazas con las del preload-queue (`[PRELOAD-Q]`).
 */
function authLog(stage: string, data: Record<string, unknown>): void {
  if (!HTTP_AUTH_DEBUG) return;
  // eslint-disable-next-line no-console
  console.log(`[HTTP-AUTH] ${stage}`, {
    ts: Math.round(performance.now()),
    ...data,
  });
}

/** Contexto mínimo de una request para correlacionar trazas. */
function reqCtx(req: HttpRequest<any>): Record<string, unknown> {
  return {
    method: req.method,
    url: req.url,
    cid: req.headers.get(CORRELATION_HEADER),
  };
}

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  messages!: string[];
  private refreshTokenInProgress: boolean = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private readonly authService = inject(AuthService);
  private readonly authGate = inject(AuthRefreshGate);

  constructor(
    private notificationService: NotificationService,
    private loginService: LoginService,
    private sessionStorageService: SessionStorageService
  ) {
    this.messages = [];
  }

  /**
   * @description Intercepta todos los HttpErrorResponse del sistema
   * y muestra el mensaje correspondiente al usuario segun su status.
   * @param req
   * @param next
   */
  intercept(req: HttpRequest<any>, next: HttpHandler) {
    req = this.addAuthenticationToken(req);
    return next.handle(req).pipe(
      catchError((errorResponse: any) => {
        const ctx = reqCtx(req);
        authLog('catchError:enter', {
          ...ctx,
          status: errorResponse?.status,
          backendCode: errorResponse?.error?.code,
          refreshInProgress: this.refreshTokenInProgress,
        });
        if(!navigator.onLine || errorResponse?.code === "ECONNREFUSED"){
          authLog('branch:offline-or-econnrefused', {
            ...ctx,
            online: navigator.onLine,
          });
          this.messages.push(
            'Hubo un error de conexión con nuestro servidor. Por favor, intente nuevamente o solicite soporte.'
          );
          this.notificationService.showMessage(this.messages, 'error');
          setTimeout(() => {
            this.messages = [];
            this.notificationService.clearMessages();
          }, 4000);
        }

        if (
          errorResponse &&
          (errorResponse.status === 401 || errorResponse.status === 403)
        ) {
          // El token solo se descarta en errores de AUTH: acá el refresh
          // (o el logout) lo repone. Borrarlo en cualquier error (404/500/
          // offline) dejaba la sesión muerta, la siguiente request salía
          // sin Authorization -> 401 -> refresh + rotación innecesaria, y
          // cualquier request vieja en vuelo terminaba en 4011/4012.
          this.sessionStorageService.removeItem(environment.TOKEN);
          const error = errorResponse?.error;
          const code = error?.code;
          authLog('branch:401-403 -> token wiped', { ...ctx, status: errorResponse.status, backendCode: code });
          // Token invalidado por doble session
          if(code === 4011 || code === 4012) {
            authLog('FORCE-LOGOUT (double session)', { ...ctx, backendCode: code });
            this.authService.forceLogout();
            return throwError(() => errorResponse);
          }
          // Si el que falló es el refresh endpoint mismo, NO entrar
          // a la lógica de waiter/leader - sería deadlock recursivo
          // (el refresh quedaría esperándose a sí mismo en el
          // refreshTokenSubject). Propagar el error para que el
          // `catchError` de `refreshAccessToken` lo procese y dispare
          // logout/forceLogout.
          if (req.url.endsWith('/auth-internal/refresh/token')) {
            authLog('refresh-endpoint self-failed -> propagate', ctx);
            return throwError(() => errorResponse);
          }
          if (this.refreshTokenInProgress) {
            authLog('role:WAITER (refresh already running)', ctx);
            return this.refreshTokenSubject.pipe(
              filter((result) => result !== null),
              take(1),
              switchMap(() => {
                authLog('WAITER released -> retry', ctx);
                return next.handle(this.addAuthenticationToken(req));
              })
            );
          } else {
            authLog('role:LEADER (starting refresh)', ctx);
            this.refreshTokenInProgress = true;
            // Abrimos el gate: a partir de acá los consumidores en pausa
            // (PreloadQueueService) NO disparan nuevas requests ni
            // preemptan la actual, así no se reinicia el refresh.
            this.authGate.begin();
            this.refreshTokenSubject.next(null);
            return this.refreshAccessToken().pipe(
              switchMap((data: any) => {
                // `refreshAccessToken` devuelve el sentinel string 'error'
                // cuando el refresh endpoint falló (401/expirado). En ese
                // caso NO hay token nuevo: echamos al usuario y propagamos
                // el error original en vez de reintentar (reintentar con el
                // token borrado vuelve a 401 en loop).
                if (data === 'error' || !data?.data?.jwt) {
                  authLog('LEADER refresh FAILED -> force logout', ctx);
                  this.authService.forceLogout();
                  return throwError(() => errorResponse);
                }
                authLog('LEADER refresh OK -> retry + release waiters', {
                  ...ctx,
                  gotFreshToken: !!data?.data?.jwt,
                });
                this.refreshTokenSubject.next(data.data.jwt);
                return next.handle(this.addAuthenticationToken(req));
              }),
              // Cerramos el gate cuando el retry del request terminó (ok,
              // error o cancelación). Ese es el evento que habilita a la
              // cola a continuar con el siguiente item.
              finalize(() => {
                this.refreshTokenInProgress = false;
                this.authGate.end();
              })
            );
          }
        } else if (errorResponse && errorResponse.status === 500) {
          authLog('branch:500 (no refresh, token untouched)', {
            ...ctx,
            status: 500,
          });
          let errorMessage: any;
          const message = errorResponse.error.message ?? errorResponse.error.errors[0] ?? "";
          errorMessage = `Error: ${message}`;

          return throwError(() => errorMessage);
        } else {
          authLog('branch:other (no refresh, token untouched)', {
            ...ctx,
            status: errorResponse?.status,
          });
          return throwError(() => errorResponse);
        }
      })
    );
  }

  private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    const headers = request.headers;
    const auth = headers.get('Authorization');
    if(auth && request.url.endsWith('/auth/refresh/token')) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.sessionStorageService.getItem(
            environment.REFRESH_TOKEN
          )}`,
        },
      });
    }
    if (!this.sessionStorageService.getItem(environment.TOKEN)) {
      return request;
    }
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${this.sessionStorageService.getItem(
          environment.TOKEN
        )}`,
      },
    });
  }

  private refreshAccessToken(): Observable<any> {
    const token = this.sessionStorageService.getItem(
      environment.REFRESH_TOKEN
    );
    const setHeaders = {
      Authorization: `Bearer ${token}`,
    };
    authLog('refreshAccessToken:start', { hasRefreshToken: !!token });
    return this.loginService.refreshToken(setHeaders).pipe(
      switchMap((data: any) => {
        // Rotación: backend emite un refresh token NUEVO acá. Cualquier
        // request vieja en vuelo con el token previo puede ser marcada
        // como doble sesión cuando llegue tarde.
        authLog('refreshAccessToken:OK (tokens ROTATED)', {
          gotJwt: !!data?.data?.jwt,
          gotRefresh: !!data?.data?.refreshToken,
        });
        this.sessionStorageService.setItem(environment.TOKEN, data.data.jwt);
        this.sessionStorageService.setItem(
          environment.REFRESH_TOKEN,
          data.data.refreshToken
        );
        this.authService.tokenLS = data.data.jwt;
        this.refreshTokenInProgress = false;
        return of(data);
      }),
      catchError((err) => {
        authLog('refreshAccessToken:FAILED', { error: err?.status ?? String(err) });
        this.refreshTokenInProgress = false;
        const errorMessage = err?.error?.errors?.[0] ?? err.error?.message ?? err?.errors?.[0] ?? err?.message ?? err ?? "";
        if(errorMessage?.includes("Query did not return a unique result: 2") || errorMessage?.includes("no found token")) {
          this.authService.logout();
          this.notificationService.showMessage(["Hemos detectado un problema con tu sesión. Por favor, vuelve a iniciar sesión."], 'error');
         } else {
          // Cualquier otra falla de refresh = sesión inválida.
          // Antes caía a `of('error')` sin logout y el usuario
          // quedaba en la página con la sesión muerta.
          this.authService.forceLogout();
         }
        return of('error');
      })
    );
  }
}
